import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Plus, Upload, FileText, History, CheckCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['ID', 'SERVICE_AGREEMENT', 'CONSENT', 'POLICY', 'HR', 'INCIDENT_DOC', 'OTHER'];

export default function DocumentTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [showUploadVersion, setShowUploadVersion] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/templates?page=${page}&pageSize=20`);
      setTemplates(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return (
    <div className="space-y-6" data-testid="document-templates-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Document Templates</h1>
          <p className="text-muted-foreground">Manage reusable document templates</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="create-template-btn"><Plus className="h-4 w-4 mr-2" />Create Template</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create Template</DialogTitle></DialogHeader>
            <CreateTemplateForm onSuccess={() => { setShowCreate(false); fetchTemplates(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template No</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Active Version</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : templates.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No templates found</TableCell></TableRow>
              ) : templates.map(tpl => (
                <TableRow key={tpl.id} className="hover:bg-muted/50" data-testid={`template-row-${tpl.id}`}>
                  <TableCell className="font-mono text-sm">{tpl.templateNo}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{tpl.name}</span>
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={tpl.category} /></TableCell>
                  <TableCell>
                    {tpl.currentVersion ? (
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                        v{tpl.currentVersion}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">No version</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{tpl.createdAt?.slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setSelectedTemplate(tpl); setShowVersions(true); }}
                        data-testid={`versions-${tpl.id}`}
                      >
                        <History className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setSelectedTemplate(tpl); setShowUploadVersion(true); }}
                        data-testid={`upload-version-${tpl.id}`}
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > 20 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">Page {page} of {Math.ceil(total / 20)}</span>
          <Button variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Version History - {selectedTemplate?.name}</DialogTitle></DialogHeader>
          {selectedTemplate && <VersionHistory template={selectedTemplate} />}
        </DialogContent>
      </Dialog>

      <Dialog open={showUploadVersion} onOpenChange={setShowUploadVersion}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Upload New Version - {selectedTemplate?.name}</DialogTitle></DialogHeader>
          {selectedTemplate && (
            <UploadVersionForm
              templateId={selectedTemplate.id}
              currentVersion={selectedTemplate.currentVersion}
              onSuccess={() => { setShowUploadVersion(false); fetchTemplates(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateTemplateForm({ onSuccess }) {
  const [form, setForm] = useState({ name: '', description: '', category: '' });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) {
      toast.error('Name and category are required');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('category', form.category);
      if (form.description) formData.append('description', form.description);
      if (file) formData.append('file', file);
      await api.post('/templates', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Template created successfully');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Name *</Label>
        <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Template name" />
      </div>
      <div>
        <Label>Category *</Label>
        <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
          <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description" rows={3} />
      </div>
      <div>
        <Label>Initial Version File (optional)</Label>
        <Input type="file" onChange={(e) => setFile(e.target.files[0])} accept=".pdf,.doc,.docx" />
      </div>
      <Button type="submit" disabled={loading} className="w-full">{loading ? 'Creating...' : 'Create Template'}</Button>
    </form>
  );
}

function UploadVersionForm({ templateId, currentVersion, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/templates/${templateId}/version`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Version ${(currentVersion || 0) + 1} uploaded successfully`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to upload version');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 bg-muted rounded-lg">
        <p className="text-sm">Current version: <span className="font-medium">v{currentVersion || 'None'}</span></p>
        <p className="text-sm text-muted-foreground">New version will be: <span className="font-medium">v{(currentVersion || 0) + 1}</span></p>
      </div>
      <div>
        <Label>File *</Label>
        <Input type="file" onChange={(e) => setFile(e.target.files[0])} accept=".pdf,.doc,.docx" />
      </div>
      <Button type="submit" disabled={loading} className="w-full">{loading ? 'Uploading...' : 'Upload Version'}</Button>
    </form>
  );
}

function VersionHistory({ template }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/templates/${template.id}/versions`)
      .then(r => setVersions(r.data.items || r.data.data || []))
      .catch(() => toast.error('Failed to load versions'))
      .finally(() => setLoading(false));
  }, [template.id]);

  const handleDownload = async (version) => {
    try {
      const res = await api.get(`/templates/${template.id}/versions/${version.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = version.fileName || `${template.name}_v${version.version}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download version');
    }
  };

  if (loading) return <div className="py-8 text-center">Loading...</div>;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Version</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>File Name</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {versions.length === 0 ? (
          <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No versions</TableCell></TableRow>
        ) : versions.map(v => (
          <TableRow key={v.id}>
            <TableCell className="font-medium">v{v.version || v.versionNumber}</TableCell>
            <TableCell><StatusBadge status={v.status} /></TableCell>
            <TableCell className="truncate max-w-[200px]">{v.fileName || '-'}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{v.createdAt?.slice(0, 10)}</TableCell>
            <TableCell>
              <Button size="sm" variant="ghost" onClick={() => handleDownload(v)}>
                <Eye className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
